import { useCallback, useMemo, Fragment } from 'react';
import styled from '@emotion/styled';
import type { Chat } from '@trinity/chat';
import { ChatActionType } from '@trinity/chat';
import { QueryFilesSuggestedAction } from './Message.SuggestedActions.Files';
import { SurveyQuestionSuggestedActions } from './Message.SuggestedActions.Question';

type Props = {
  item: Chat.Attachment.SuggestedActions;
  className?: string;
};

export const SuggestedActionsAttachment = (props: Props) => {
  const renderItem = useCallback(
    (item: Chat.Attachment.SuggestedChatAction) => {
      switch (item.type) {
        case ChatActionType.QuerySurveyQuestion:
          return null;

        case ChatActionType.QueryFiles:
          return <QueryFilesSuggestedAction item={item} query={props.item.query} />;

        default:
          throw new Error(
            `Unsupported suggested action type: ${(item as Chat.Attachment.SuggestedChatAction).type}`
          );
      }
    },
    [props.item.query]
  );

  const questionSuggestions = useMemo(() => {
    const items: Chat.Attachment.AskSurveyQuestionSuggestedAction[] = [];
    props.item.items.forEach((item) => {
      if (item.type === ChatActionType.QuerySurveyQuestion) {
        items.push(item);
      }
    });
    return items;
  }, [props.item.items]);

  const renderSurveyActions = useCallback(() => {
    if (!questionSuggestions.length) return null;

    return <SurveyQuestionSuggestedActions query={props.item.query} items={questionSuggestions} />;
  }, [questionSuggestions, props.item.query]);

  return (
    <StyledRoot className={props.className}>
      {renderSurveyActions()}
      {props.item.items.map((item, i) => (
        <Fragment key={i}>{renderItem(item)}</Fragment>
      ))}
    </StyledRoot>
  );
};

const StyledRoot = styled.div(({ theme }) => ({
  '& > *': {
    marginBottom: 15,
  },

  '& > *:last-child': {
    marginBottom: 0,
  },
}));
